import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

export interface TooltipAnchor {
  x: number;
  y: number;
}

export interface TooltipProps {
  anchor: TooltipAnchor | null;
  children?: ReactNode;
  /** Pixel offset from the anchor. Default { x: 14, y: 14 }. */
  offset?: TooltipAnchor;
  /** Minimum width. */
  minWidth?: number;
}

const baseStyle: CSSProperties = {
  position: "fixed",
  pointerEvents: "none",
  background: "rgba(15,18,24,0.92)",
  border: "1px solid rgba(255,255,255,0.16)",
  borderRadius: 6,
  color: "#e6e8ee",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 12,
  lineHeight: 1.35,
  padding: "6px 10px",
  zIndex: 1100,
  boxShadow: "0 4px 16px rgba(0,0,0,0.45)",
};

export function Tooltip({ anchor, children, offset, minWidth }: TooltipProps) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    setSize({ width: window.innerWidth, height: window.innerHeight });
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!anchor) return null;
  const off = offset ?? { x: 14, y: 14 };

  // Flip so it stays on screen
  const desiredX = anchor.x + off.x;
  const desiredY = anchor.y + off.y;
  const flipX = desiredX > size.width - 220;
  const flipY = desiredY > size.height - 80;
  const left = flipX ? Math.max(8, anchor.x - off.x - 200) : desiredX;
  const top = flipY ? Math.max(8, anchor.y - off.y - 60) : desiredY;

  return (
    <div role="tooltip" style={{ ...baseStyle, left, top, minWidth: minWidth ?? 120 }}>
      {children}
    </div>
  );
}
